import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import SeoHead from '../../components/feature/SeoHead';
import StripePaymentForm from './components/StripePaymentForm';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '');
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const bookingSchema = {
  '@context': 'https://schema.org',
  '@type': 'ReserveAction',
  name: 'Book a Doula Consultation — Lotus Maternal Brooklyn NY',
  description: 'Book your personalized doula care package with Lotus Maternal. Free 30-minute consultation available for all families.',
  url: `${siteUrl}/booking`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Book', item: `${siteUrl}/booking` },
    ],
  },
  target: {
    '@type': 'EntryPoint',
    urlTemplate: `${siteUrl}/booking`,
    actionPlatform: [
      'http://schema.org/DesktopWebPlatform',
      'http://schema.org/MobileWebPlatform',
    ],
  },
  object: {
    '@type': 'Service',
    name: 'Doula Services',
    provider: {
      '@type': 'MedicalBusiness',
      name: 'Lotus Maternal',
      url: siteUrl,
      telephone: '(929)473-8867',
      email: 'support@lotusmaternal.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Brooklyn',
        addressRegion: 'NY',
        addressCountry: 'US',
      },
    },
  },
};

function ConsultationEmailBanner() {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [open, setOpen] = useState(true);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      const response = await fetch('https://readdy.ai/api/form/d6jfdircs59vsrrrk1j0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (!open) return null;

  return (
    <div className="bg-gradient-to-r from-amber-700 to-green-800 text-white px-6 py-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => setOpen(false)}
          className="float-right text-white/60 hover:text-white transition-colors cursor-pointer mt-1"
          aria-label="Dismiss"
        >
          <i className="ri-close-line text-xl"></i>
        </button>
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-mail-heart-line text-2xl text-white"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-lg mb-1">Join Our Consultation Email List</h3>
            <p className="text-white/80 text-sm">
              Get tips, resources, and updates about doula services — straight to your inbox.
            </p>
          </div>
          {status === 'success' ? (
            <div className="flex items-center gap-2 bg-white/20 rounded-xl px-5 py-3">
              <i className="ri-checkbox-circle-fill text-green-300 text-xl"></i>
              <span className="font-semibold text-sm">You're on the list! 🌸</span>
            </div>
          ) : (
            <form
              id="consultation-email-signup-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-2 w-full md:w-auto"
            >
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Your name"
                className="px-4 py-2.5 rounded-xl text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 w-full sm:w-36"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="your@email.com"
                className="px-4 py-2.5 rounded-xl text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 w-full sm:w-48"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="bg-white text-amber-700 font-bold px-5 py-2.5 rounded-xl hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 text-sm"
              >
                {status === 'loading' ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  'Subscribe'
                )}
              </button>
              {status === 'error' && (
                <p className="text-red-300 text-xs mt-1 sm:hidden">Something went wrong. Try again.</p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const preselectedPackage = searchParams.get('package');

  const [step, setStep] = useState(preselectedPackage ? 2 : 1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    package: preselectedPackage || '',
    dueDate: '',
    message: '',
  });

  const [clientSecret, setClientSecret] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);

  useEffect(() => {
    if (preselectedPackage) {
      setFormData((prev) => ({ ...prev, package: preselectedPackage }));
      setStep(2);
    }
  }, [preselectedPackage]);

  const packages = [
    { name: 'Princess', price: 600 },
    { name: 'Queen', price: 800 },
    { name: 'Empress', price: 1000 },
    { name: 'Goddess', price: 1200 },
    { name: 'Prenatal', price: 2500 },
    { name: 'Postpartum', price: 2000 },
    { name: 'Full Journey', price: 5000 },
  ];

  const selectedPackageData = packages.find((pkg) => pkg.name === formData.package);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNextStep = async () => {
    if (step === 1 && formData.name && formData.email && formData.phone && formData.package) {
      setIsLoadingPayment(true);
      try {
        const response = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            amount: Math.round((selectedPackageData?.price || 0) * 100),
            currency: 'usd',
            productType: 'package',
            packageName: formData.package,
            customerEmail: formData.email,
            customerName: formData.name,
          }),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to create payment intent');
        }

        const data = await response.json();
        if (!data.clientSecret) throw new Error('No client secret returned');

        setClientSecret(data.clientSecret);
        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        console.error('Error creating payment intent:', error);
        alert('Unable to initialize payment. Please try again.');
      } finally {
        setIsLoadingPayment(false);
      }
    }
  };

  const handlePaymentSuccess = () => {
    setShowConfirmation(true);
  };

  const appearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#b45309',
      colorBackground: '#ffffff',
      colorText: '#262626',
      colorDanger: '#dc2626',
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '12px',
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-green-50">
      <SeoHead
        title="Book Your Doula — Free Consultation | Lotus Maternal Brooklyn NY"
        description="Book your doula consultation with Lotus Maternal in Brooklyn, NY. Choose from pregnancy, birth, and postpartum packages starting at $600. Free 30-minute consultation available for all families."
        keywords="book doula Brooklyn NY, doula consultation booking, pregnancy support appointment, birth doula booking, postpartum care appointment New York"
        canonical={`${siteUrl}/booking`}
        ogTitle="Book Your Doula Consultation | Lotus Maternal"
        ogDescription="Book your personalized doula care package with Lotus Maternal. Free 30-minute consultation available. Packages starting at $600."
        schema={bookingSchema}
      />
      {/* Hero */}
      <div className="relative h-64 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/42f9f873ab0226d0c02c632d0a03b1a2.jpg"
          alt="Book Your Journey"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-display text-5xl lg:text-6xl font-bold text-white drop-shadow-lg mb-3">
            Book Your Journey
          </h1>
          <p className="text-white/90 text-lg max-w-xl drop-shadow-md">
            Let's begin your personalized doula care experience
          </p>
        </div>
      </div>

      <ConsultationEmailBanner />

      <div className="max-w-4xl mx-auto px-6 py-16">
        {!showConfirmation ? (
          <>
            {/* Call Us nudge */}
            <div className="flex items-center justify-center mb-10">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 flex flex-col sm:flex-row items-center gap-4 shadow-sm w-full max-w-xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                  <i className="ri-phone-line text-amber-700 text-xl"></i>
                </div>
                <div className="text-center sm:text-left flex-1">
                  <p className="font-semibold text-neutral-800 text-sm">Prefer to talk first?</p>
                  <p className="text-neutral-500 text-xs mt-0.5">Our team is available 24/7 to answer any questions before you book.</p>
                </div>
                <a
                  href="tel:9294738867"
                  className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white font-bold px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm shadow"
                >
                  <i className="ri-phone-fill text-sm"></i>
                  Call Us
                </a>
              </div>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <div className={`flex items-center gap-2 ${step === 1 ? 'text-amber-700' : 'text-neutral-400'}`}>
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full ${
                    step === 1 ? 'bg-amber-700 text-white' : 'bg-neutral-200 text-neutral-500'
                  } font-bold`}
                >
                  1
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">Your Details</span>
              </div>
              <div className="w-16 h-0.5 bg-neutral-200"></div>
              <div className={`flex items-center gap-2 ${step === 2 ? 'text-amber-700' : 'text-neutral-400'}`}>
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full ${
                    step === 2 ? 'bg-amber-700 text-white' : 'bg-neutral-200 text-neutral-500'
                  } font-bold`}
                >
                  2
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">Payment</span>
              </div>
            </div>

            {/* Step 1: Contact Info */}
            {step === 1 && (
              <div className="bg-white rounded-3xl shadow-xl p-10 border border-amber-100">
                <h2 className="font-display text-3xl font-bold text-neutral-800 mb-8">
                  Tell Us About You
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-5 py-3.5 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3.5 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-5 py-3.5 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        placeholder="(123) 456-7890"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Select Package *
                    </label>
                    <select
                      name="package"
                      value={formData.package}
                      onChange={handleInputChange}
                      className="w-full px-5 py-3.5 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm cursor-pointer"
                    >
                      <option value="">Choose a package</option>
                      {packages.map((pkg) => (
                        <option key={pkg.name} value={pkg.name}>
                          {pkg.name} - ${pkg.price.toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Due Date (if applicable)
                    </label>
                    <input
                      type="date"
                      name="dueDate"
                      value={formData.dueDate}
                      onChange={handleInputChange}
                      className="w-full px-5 py-3.5 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-5 py-3.5 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm resize-none"
                      placeholder="Tell us anything else we should know..."
                      maxLength={500}
                    ></textarea>
                  </div>
                  <button
                    onClick={handleNextStep}
                    disabled={!formData.name || !formData.email || !formData.phone || !formData.package || isLoadingPayment}
                    className="w-full py-4 bg-gradient-to-r from-amber-700 to-amber-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isLoadingPayment ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Loading...
                      </span>
                    ) : (
                      'Continue to Payment'
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Payment */}
            {step === 2 && clientSecret && selectedPackageData && stripePromise && (
              <div className="space-y-6">
                {/* Order Summary */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-amber-100">
                  <h3 className="font-display text-2xl font-bold text-neutral-800 mb-6">
                    Order Summary
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-neutral-200">
                      <div>
                        <p className="font-semibold text-neutral-800">{formData.package} Package</p>
                        <p className="text-sm text-neutral-500">{formData.name}</p>
                      </div>
                      <p className="font-display text-2xl font-bold text-amber-700">
                        ${selectedPackageData.price.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex justify-between items-center text-sm text-neutral-600">
                      <span>Email:</span>
                      <span>{formData.email}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-neutral-600">
                      <span>Phone:</span>
                      <span>{formData.phone}</span>
                    </div>
                    {formData.dueDate && (
                      <div className="flex justify-between items-center text-sm text-neutral-600">
                        <span>Due Date:</span>
                        <span>{formData.dueDate}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stripe Payment Form */}
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-amber-100">
                  <h3 className="font-display text-2xl font-bold text-neutral-800 mb-6">Payment Details</h3>
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance,
                      loader: 'always',
                    }}
                  >
                    <StripePaymentForm
                      amount={selectedPackageData.price}
                      packageName={formData.package}
                      customerEmail={formData.email}
                      onSuccess={handlePaymentSuccess}
                      onBack={() => {
                        setStep(1);
                        setClientSecret('');
                      }}
                    />
                  </Elements>
                </div>
              </div>
            )}
          </>
        ) : (
          // Confirmation
          <div className="bg-white rounded-3xl shadow-xl p-12 border border-amber-100 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-5xl text-green-600"></i>
            </div>
            <h2 className="font-display text-4xl font-bold text-neutral-800 mb-4">
              Booking Confirmed!
            </h2>
            <p className="text-neutral-600 mb-8 max-w-xl mx-auto">
              Thank you, <strong>{formData.name}</strong>! Your booking for the{' '}
              <strong>{formData.package} Package</strong> has been received. We'll contact you at{' '}
              <strong>{formData.email}</strong> within 24 hours to finalize details.
            </p>
            <div className="bg-gradient-to-r from-amber-50 to-green-50 rounded-2xl p-6 mb-8">
              <p className="text-sm text-neutral-700">
                <strong>Next Steps:</strong> Check your email for a confirmation and calendar invite.
                We can't wait to support you on this journey! 🌸
              </p>
            </div>
            <a
              href="/"
              className="inline-block px-10 py-3.5 bg-gradient-to-r from-amber-700 to-amber-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
            >
              Return Home
            </a>
          </div>
        )}
      </div>
    </div>
  );
}